import { DatePipe } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';

import { UtilService } from '../../../../../../../shared/services/util.service';
import { AbntPipe } from '../../../../../../../shared/pipes/abnt.pipe';
import { CpfCnpjPipe } from '../../../../../../../shared/pipes/cpf-cnpj.pipe';
import { FoneMaskPipe } from '../../../../../../../shared/pipes/fone-mask.pipe';
import { AlertCardComponent, AlertType } from '../../../../../../../shared/components/alert-card/alert-card.component';
import { TabsScrollDirective } from '../../../../../../../shared/directives/tabs-scroll.directive';
import { TipoPessoaEnum } from '../../../../../../../shared/enums/pessoa/tipo-pessoa.enum';
import { ClienteDetalheStruct } from '../../../../../../../shared/services/structs/vendas/cliente-detalhe.struct';
import { HistoricoClienteStruct } from '../../../../../../../shared/services/structs/vendas/historico-cliente.struct';
import { VendaDoClienteStruct } from '../../../../../../../shared/services/structs/vendas/venda-do-cliente.struct';
import { KanbanCor } from '../../../../../../../shared/services/models/vendas/kanban-cor.model';
import { VendaTipoDocumentoEnum } from '../../../../../../../shared/enums/vendas/venda-tipo-documento.enum';
import { PvCorVm, resolverCor } from '@shared/helpers/kanban-cor.helper';

/** As abas do modal. `vendas` aguarda a PO definir o que a aba mostra. */
export type AbaClienteDialog = 'dados' | 'vendas' | 'credito' | 'contatos';

export interface VdClienteDialogData {
    /**
     * O cliente PRONTO, e não um id para buscar. Quem já carregou foi o card, no momento em que
     * o cliente foi escolhido — buscar de novo aqui só criaria uma segunda fonte para os mesmos
     * números e um loading que ninguém precisa ver.
     */
    cliente: ClienteDetalheStruct;

    /** As últimas compras, já carregadas. Null quando a consulta falhou lá atrás. */
    historico: HistoricoClienteStruct | null;

    /** Catálogo de cores do kanban, vindo do agregado de configurações da tela. */
    cores: KanbanCor[];

    /** Qual aba abre primeiro. Os três links do card mandam abas diferentes. */
    aba?: AbaClienteDialog;
    /**
     * Se o documento em edição RECUSA cliente com título vencido. Quem sabe disso é a tela — a
     * regra depende do tipo do documento e da configuração da empresa —, e o modal só precisa da
     * resposta para dizer ao usuário se ele pode ou não seguir com este cliente.
     */
    bloqueiaPorAtraso?: boolean;
}

/** Um campo da aba Dados: rótulo em caixa alta e o valor abaixo. */
interface CampoDado {
    label: string;
    valor: string;
    /** Qual pipe aplica na exibição. Nenhum, para os campos que já são texto. */
    formato?: 'documento' | 'telefone' | 'data';
}

/** O aviso do topo: o que o usuário precisa decidir antes de olhar qualquer aba. */
interface AvisoAtraso {
    tipo: AlertType;
    titulo: string;
    texto: string;
}

/**
 * Modal de detalhes do cliente (§5.2).
 *
 * UM modal com quatro abas, e não quatro modais: os três links do card — "dados do cliente",
 * "ver últimas vendas" e "pessoas de contato" — abrem este mesmo diálogo e só escolhem a aba
 * inicial, exatamente como o protótipo faz (`abrirClienteDlg(cliente, aba)`).
 *
 * Estrutura no padrão da casa: `.modal-header` com `mat-dialog-title`, corpo em
 * `mat-dialog-content`, rodapé em `mat-dialog-actions`, abas em `mat-tab-group` (estilo
 * centralizado no `styles.css`, com a directive de scroll) e aviso em `app-alert-card`.
 */
@Component({
    selector: 'app-vd-cliente-dialog',
    standalone: true,
    imports: [
        AbntPipe,
        DatePipe,
        CpfCnpjPipe,
        FoneMaskPipe,
        MatDialogTitle,
        MatDialogContent,
        MatDialogActions,
        MatButtonModule,
        MatIconModule,
        MatTabsModule,
        MatTableModule,
        TabsScrollDirective,
        AlertCardComponent,
    ],
    templateUrl: './vd-cliente-dialog.component.html',
    styleUrl: './vd-cliente-dialog.component.css',
})
export class VdClienteDialogComponent implements OnInit {
    private readonly dialogRef = inject(MatDialogRef<VdClienteDialogComponent>);
    public readonly data = inject<VdClienteDialogData>(MAT_DIALOG_DATA);

    /** A ordem das abas no template. Serve para traduzir a aba pedida em índice. */
    private readonly ordemDasAbas: AbaClienteDialog[] = ['dados', 'vendas', 'credito', 'contatos'];

    private readonly cpfCnpjPipe = new CpfCnpjPipe();

    public abaInicial: number = 0;

    public readonly colunasVendas: string[] = ['documento', 'dataVendedor', 'valor', 'etapa'];

    public get cliente(): ClienteDetalheStruct {
        return this.data.cliente;
    }

    public get historico(): HistoricoClienteStruct | null {
        return this.data.historico;
    }

    public ngOnInit(): void {
        const pedida = this.data.aba ?? 'dados';
        const indice = this.ordemDasAbas.indexOf(pedida);

        this.abaInicial = indice >= 0 ? indice : 0;
    }

    /** "#ORC-412" / "#PED-412" — o mesmo rótulo que o board usa nos cards. */
    public rotuloDocumento(venda: VendaDoClienteStruct): string {
        const sigla = venda.idVendaTipoDocumento === VendaTipoDocumentoEnum.Orcamento ? 'ORC' : 'PED';

        return '#' + sigla + '-' + venda.numero;
    }

    /**
     * A cor da etapa pelo MESMO resolvedor do board — o catálogo traz um trio para o tema claro
     * e outro para o escuro, e o tema de agora é o que vale.
     */
    public corDaEtapa(venda: VendaDoClienteStruct): PvCorVm {
        return resolverCor(venda.idKanbanCor, this.data.cores, document.body.classList.contains('dark-mode'));
    }

    /**
     * O aviso de título vencido, escrito do ponto de vista de quem monta o documento: ele precisa
     * saber se PODE usar este cliente, e não como a empresa configurou o sistema.
     */
    public get avisoAtraso(): AvisoAtraso | null {
        if (!this.cliente?.temTitulosEmAtraso) return null;

        const vencido = this.cliente.valorVencido;
        const quanto =
            vencido > 0
                ? 'São R$ ' +
                  vencido.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) +
                  ' vencidos. '
                : '';

        if (this.data.bloqueiaPorAtraso) {
            return {
                tipo: 'error',
                titulo: 'Este cliente não pode receber o pedido',
                texto:
                    quanto +
                    'Enquanto os títulos não forem regularizados, o pedido de venda para este cliente não pode ser gravado.',
            };
        }

        return {
            tipo: 'warning',
            titulo: 'Cliente com títulos vencidos',
            texto: quanto + 'O documento pode ser gravado normalmente — a cobrança é assunto do financeiro.',
        };
    }

    /** A linha abaixo do nome no cabeçalho: documento (com máscara de CPF/CNPJ) e localidade, o que houver. */
    public get linhaResumo(): string {
        const documento = this.cliente?.documento ? this.cpfCnpjPipe.transform(this.cliente.documento) : null;
        const partes = [documento, this.cliente?.localidade].filter(Boolean);

        return partes.length ? partes.join(' · ') : 'Sem documento nem endereço cadastrados';
    }

    public get rotuloTipoPessoa(): string {
        switch (this.cliente?.idTipoPessoa) {
            case TipoPessoaEnum.Fisica:
                return 'Física';
            case TipoPessoaEnum.Juridica:
                return 'Jurídica';
            case TipoPessoaEnum.Estrangeiro:
                return 'Estrangeiro';
            default:
                return '—';
        }
    }

    /**
     * Os campos da aba Dados. Campo vazio vira travessão em vez de sumir: a ausência também é
     * informação — quem olha precisa saber que o cadastro está incompleto.
     */
    public get dados(): CampoDado[] {
        const c = this.cliente;

        if (!c) return [];

        const endereco = [c.logradouro, c.numero].filter(Boolean).join(', ');
        const ehJuridica = c.idTipoPessoa === TipoPessoaEnum.Juridica;

        return [
            { label: ehJuridica ? 'Razão social' : 'Nome', valor: c.nome || '—' },
            { label: 'Nome fantasia', valor: c.nomeFantasia || '—' },
            {
                label: ehJuridica ? 'CNPJ' : 'CPF',
                valor: c.documento || '—',
                formato: 'documento',
            },
            { label: 'Tipo de pessoa', valor: this.rotuloTipoPessoa },
            { label: 'Endereço', valor: endereco || '—' },
            { label: 'Bairro', valor: c.bairro || '—' },
            { label: 'Cidade / UF', valor: c.localidade || '—' },
            { label: 'CEP', valor: c.cep || '—' },
            { label: 'Telefone', valor: c.telefone || '—', formato: 'telefone' },
            { label: 'E-mail', valor: c.email || '—' },
            // Sem data não há o que formatar: o pipe `date` estoura com um travessão na entrada.
            {
                label: 'Cliente desde',
                valor: c.clienteDesde || '—',
                formato: c.clienteDesde ? 'data' : undefined,
            },
        ];
    }

    /** Quanto do limite já está comprometido. Sem limite cadastrado, não há percentual. */
    public get percentualUsado(): number | null {
        const limite = this.cliente?.limiteCredito ?? 0;

        if (!limite) return null;

        return Math.min(100, UtilService.arredondarAbnt(((this.cliente?.valorEmAberto ?? 0) / limite) * 100, 0));
    }

    /**
     * A situação do limite. A `classe` pinta a barra; o `badge` é a variante de cor do
     * `status-badge` do sistema, e não uma paleta própria.
     */
    public get situacaoDoLimite(): { label: string; icon: string; classe: string; badge: string } {
        const pct = this.percentualUsado;

        if (pct == null) {
            return { label: 'Sem limite definido', icon: 'info', classe: 'neutro', badge: 'badge-cinza' };
        }

        if (pct >= 90) {
            return { label: 'Limite comprometido', icon: 'error', classe: 'critico', badge: 'badge-vermelho' };
        }

        if (pct >= 70) {
            return { label: 'Perto do limite', icon: 'warning', classe: 'atencao', badge: 'badge-amarelo' };
        }

        return { label: 'Limite disponível', icon: 'check_circle', classe: 'ok', badge: 'badge-verde' };
    }

    public fechar(): void {
        this.dialogRef.close();
    }
}
